import { useState, useEffect, useCallback } from 'react'
import { Plus, X, ChevronRight } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { formatDistanceToNow, format } from 'date-fns'
import { isActiveProfile } from './constants'

const URGENCY = [
  { value: 'low', label: 'Low', color: '#94a3b8', bg: '#f1f5f9' },
  { value: 'medium', label: 'Medium', color: '#2a6e84', bg: '#e8f5fa' },
  { value: 'high', label: 'High', color: '#c4962a', bg: '#fff8e1' },
  { value: 'critical', label: 'Critical', color: '#c94f4f', bg: '#fdf0f0' },
]
const TICKET_STATUS = [
  { value: 'open', label: 'Open', color: '#c94f4f', bg: '#fdf0f0' },
  { value: 'in_progress', label: 'In Progress', color: '#2a6e84', bg: '#e8f5fa' },
  { value: 'resolved', label: 'Resolved', color: '#2e6e3a', bg: '#e8f5eb' },
]
const getUrgency = v => URGENCY.find(u => u.value === v)
const getTicketStatus = v => TICKET_STATUS.find(s => s.value === v)

function NewTicketModal({ clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState({
    title: '', description: '', client_id: '', urgency: 'medium', assigned_to: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.title.trim()) { toast('Title required', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('tickets').insert({
      ...form,
      submitted_by: currentUser.id,
      client_id: form.client_id || null,
      assigned_to: form.assigned_to || null,
    })
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }

    // Notify assigned person
    if (form.assigned_to && form.assigned_to !== currentUser.id) {
      await supabase.from('notifications').insert({
        user_id: form.assigned_to,
        task_id: null,
        type: 'ticket',
        message: `New ${form.urgency} priority ticket: "${form.title}"`,
      })
    }

    toast('Ticket submitted', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Submit Escalation Ticket</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: '#fdf0f0', border: '1px solid #f5c6c6', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#8a0000' }}>
            Use this for client issues that need Supervising AD attention — poor performance, upset clients, escalations beyond normal task work.
          </div>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Client very upset about ad performance" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={4} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the situation in detail — what happened, what was communicated, what you need..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client</label>
              <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">No specific client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Urgency</label>
              <select className="form-control" value={form.urgency} onChange={e => set('urgency', e.target.value)}>
                {URGENCY.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Assign to (optional)</label>
            <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
              <option value="">Supervising AD (default)</option>
              {profiles.filter(p => isActiveProfile(p) && ['supervising_ad', 'ad'].includes(p.role)).map(p => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Submitting...' : 'Submit Ticket'}</button>
        </div>
      </div>
    </div>
  )
}

function TicketDetail({ ticket, profiles, clients, currentUser, onClose, onUpdated }) {
  const toast = useToast()
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [posting, setPosting] = useState(false)
  const [status, setStatus] = useState(ticket.status)

  const submitter = profiles.find(p => p.id === ticket.submitted_by)
  const assignee = profiles.find(p => p.id === ticket.assigned_to)
  const client = clients.find(c => c.id === ticket.client_id)
  const urg = getUrgency(ticket.urgency)
  const st = getTicketStatus(status)

  const loadComments = useCallback(async () => {
    const { data } = await supabase.from('ticket_comments')
      .select('*, author:profiles!ticket_comments_author_id_fkey(full_name)')
      .eq('ticket_id', ticket.id).order('created_at')
    setComments(data || [])
  }, [ticket.id])

  useEffect(() => { loadComments() }, [loadComments])

  const postComment = async () => {
    if (!newComment.trim()) return
    setPosting(true)
    await supabase.from('ticket_comments').insert({ ticket_id: ticket.id, author_id: currentUser.id, content: newComment.trim() })
    setNewComment('')
    loadComments()
    setPosting(false)
  }

  const updateStatus = async (newStatus) => {
    await supabase.from('tickets').update({ status: newStatus }).eq('id', ticket.id)
    setStatus(newStatus)
    // Notify submitter
    if (ticket.submitted_by !== currentUser.id) {
      await supabase.from('notifications').insert({
        user_id: ticket.submitted_by,
        task_id: null,
        type: 'ticket_update',
        message: `Your ticket "${ticket.title}" is now ${newStatus.replace('_', ' ')}`,
      })
    }
    onUpdated()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 500, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {urg && <span className="status-pill" style={{ background: urg.bg, color: urg.color }}>⚡ {urg.label}</span>}
              {st && <span className="status-pill" style={{ background: st.bg, color: st.color }}>{st.label}</span>}
              {client && <span className="badge" style={{ background: 'var(--navy-mid)', color: '#fff' }}>{client.name}</span>}
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--navy)' }}>{ticket.title}</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={14} /></button>
        </div>

        {/* Status actions */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', gap: 8 }}>
          {status === 'open' && <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)' }} onClick={() => updateStatus('in_progress')}>▶ Start Working</button>}
          {status === 'in_progress' && <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a' }} onClick={() => updateStatus('resolved')}>✓ Mark Resolved</button>}
          {status === 'resolved' && <button className="btn btn-sm btn-ghost" onClick={() => updateStatus('open')}>↩ Reopen</button>}
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 16, padding: '12px 14px', background: 'var(--cream)', borderRadius: 10 }}>
            <div><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Submitted By</div><div style={{ fontSize: 13, fontWeight: 500 }}>{submitter?.full_name || '—'}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Assigned To</div><div style={{ fontSize: 13, fontWeight: 500 }}>{assignee?.full_name || 'Supervising AD'}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Submitted</div><div style={{ fontSize: 13 }}>{format(new Date(ticket.created_at), 'MMM d, yyyy')}</div></div>
          </div>

          {ticket.description && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Description</div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
            </div>
          )}

          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--navy)' }}>Comments</div>
          {comments.map(c => (
            <div key={c.id} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{c.author?.full_name || 'Unknown'}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
              </div>
              <div style={{ background: 'var(--cream)', padding: '8px 12px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.5 }}>{c.content}</div>
            </div>
          ))}
          {comments.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 16 }}>No comments yet</div>}
        </div>

        <div style={{ borderTop: '1px solid var(--border-light)', padding: '12px 24px', display: 'flex', gap: 8 }}>
          <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), postComment())}
            placeholder="Add a comment..." rows={2}
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'var(--font-body)', fontSize: 13, resize: 'none', outline: 'none' }} />
          <button onClick={postComment} disabled={posting || !newComment.trim()}
            style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: newComment.trim() ? 'var(--navy)' : 'var(--border)', color: '#fff', cursor: 'pointer', alignSelf: 'flex-end' }}>→</button>
        </div>
      </div>
    </div>
  )
}

export default function TicketDeskView({ clients, profiles, currentUser, profile }) {
  const [tickets, setTickets] = useState([])
  const [showNew, setShowNew] = useState(false)
  const [selected, setSelected] = useState(null)
  const [filterStatus, setFilterStatus] = useState('open')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('tickets').select('*, client:clients(name), submitter:profiles!tickets_submitted_by_fkey(full_name), assignee:profiles!tickets_assigned_to_fkey(full_name)').order('created_at', { ascending: false })
    if (filterStatus !== 'all') q = q.eq('status', filterStatus)
    const { data } = await q
    setTickets(data || [])
    setLoading(false)
  }, [filterStatus])

  useEffect(() => { load() }, [load])

  const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 }
  const sorted = [...tickets].sort((a, b) => (urgencyOrder[a.urgency] || 3) - (urgencyOrder[b.urgency] || 3))

  return (
    <div>
      <div style={{ background: '#fdf0f0', border: '1px solid #f5c6c6', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#8a0000' }}>
        🎫 This is the escalation desk for client issues that need Supervising AD attention. For regular work, use Tasks.
      </div>

      <div className="filters-bar">
        {TICKET_STATUS.concat([{ value: 'all', label: 'All', color: 'var(--text-muted)', bg: 'var(--cream)' }]).map(s => (
          <button key={s.value} onClick={() => setFilterStatus(s.value)}
            style={{ padding: '6px 16px', borderRadius: 50, fontSize: 12, cursor: 'pointer', border: '1.5px solid', fontWeight: filterStatus === s.value ? 700 : 400, background: filterStatus === s.value ? s.bg : '#fff', color: filterStatus === s.value ? s.color : 'var(--text-secondary)', borderColor: filterStatus === s.value ? s.color : 'var(--border)' }}>
            {s.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-gold" onClick={() => setShowNew(true)}><Plus size={15} /> Submit Ticket</button>
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
        : sorted.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎫</div>
            <h3>No {filterStatus !== 'all' ? filterStatus : ''} tickets</h3>
            <p>Submit a ticket when a client issue needs escalation</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sorted.map(ticket => {
              const urg = getUrgency(ticket.urgency)
              const st = getTicketStatus(ticket.status)
              return (
                <div key={ticket.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${urg?.color}` }}
                  onClick={() => setSelected(ticket)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
                  <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        {urg && <span className="status-pill" style={{ background: urg.bg, color: urg.color, fontSize: 11 }}>⚡ {urg.label}</span>}
                        {ticket.client?.name && <span className="badge" style={{ background: 'var(--navy-mid)', color: '#fff', fontSize: 11 }}>{ticket.client.name}</span>}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{ticket.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Submitted by {ticket.submitter?.full_name} · {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                        {ticket.assignee && ` · Assigned to ${ticket.assignee.full_name}`}
                      </div>
                    </div>
                    {st && <span className="status-pill" style={{ background: st.bg, color: st.color, fontSize: 11, flexShrink: 0 }}>{st.label}</span>}
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      {showNew && <NewTicketModal clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load() }} />}
      {selected && <TicketDetail ticket={selected} profiles={profiles} clients={clients} currentUser={currentUser} onClose={() => setSelected(null)} onUpdated={() => { load() }} />}
    </div>
  )
}